import React from 'react'
import { useAuthentication } from '../../features/authentication/hooks/useAuthentication'
import { IMkUser } from '../../features/authentication/types'

/**
 * Hook that provides user-specific information extracted from the authentication context.
 * This follows SOLID principles by having a single responsibility and being easily testable.
 *
 * @returns {Object} User details including balances and account information
 */
export const useUserDetails = () => {
  const { session, isAuthenticated, isAuthorized } = useAuthentication()

  // Extract user information from the session
  const user = React.useMemo((): IMkUser | null => {
    const authorizedSession = session?.sessionType === 'authorized' ? session : null
    const authenticatedSession = session?.sessionType === 'authenticated' ? session : null
    const authorizedUser = authorizedSession?.authorizedUser
    const authenticatedUser = authenticatedSession?.authenticatedUser

    // Try authorized session first (fully logged in users)
    if (isAuthenticated && isAuthorized && authorizedUser) {
      return authorizedUser
    }

    // Fall back to authenticated session (partially logged in users)
    if (isAuthenticated && authenticatedUser) {
      const au = authenticatedUser
      if (
        au.first ||
        au.last ||
        au.firstName ||
        au.lastName ||
        au.profile?.firstName ||
        au.profile?.lastName
      ) {
        return authenticatedUser as IMkUser
      }
    }

    return null
  }, [isAuthenticated, isAuthorized, session])

  // Extract user balances
  const balances = React.useMemo(() => {
    if (!user) {
      return {
        totalCredits: 0,
        availableCredits: 0,
        totalPoints: 0,
        availablePoints: 0,
      }
    }

    return {
      totalCredits: Number(user.totalCredits) || 0,
      availableCredits: Number(user.availableCredits) || 0,
      totalPoints: Number(user.totalPoints) || 0,
      availablePoints: Number(user.availablePoints) || 0,
    }
  }, [user])

  // Check if user has sufficient balances for a given cost
  const hasSufficientBalance = React.useCallback(
    (costAmount: number, balanceType: 'credits' | 'points' = 'credits'): boolean => {
      if (!user) return false

      const balance = balanceType === 'credits' ? balances.availableCredits : balances.availablePoints
      return balance >= costAmount
    },
    [user, balances],
  )

  // Get user's tier information
  const userTier = React.useMemo(() => user?.tier || null, [user?.tier])

  // Get user's membership number
  const membershipNumber = React.useMemo(() => user?.memberNumber || null, [user?.memberNumber])

  // Check if user can afford a specific booking cost
  const canAffordBooking = React.useCallback(
    (bookingCost: number, useCredits: boolean = true): boolean => {
      if (!user) return false

      const balance = useCredits ? balances.availableCredits : balances.availablePoints
      return balance >= bookingCost
    },
    [user, balances],
  )

  // Get deficit amount if user cannot afford the booking
  const getBalanceDeficit = React.useCallback(
    (bookingCost: number, useCredits: boolean = true): number => {
      if (!user) return bookingCost

      const balance = useCredits ? balances.availableCredits : balances.availablePoints
      return Math.max(0, bookingCost - balance)
    },
    [user, balances],
  )

  // Support both legacy shape (first/last) and Okta/profile shape (firstName/lastName).
  const firstName = user?.first ?? (user as any)?.firstName ?? (user as any)?.profile?.firstName ?? null
  const lastName = user?.last ?? (user as any)?.lastName ?? (user as any)?.profile?.lastName ?? null

  return {
    // User state
    user,
    isAuthenticated,
    isAuthorized,

    // User details
    username: user?.username || null,
    email: user?.email || null,
    firstName,
    lastName,
    tier: userTier,
    membershipNumber,

    // Balances
    balances,

    // Utility functions
    hasSufficientBalance,
    canAffordBooking,
    getBalanceDeficit,

    // Derived state
    hasCredits: balances.availableCredits > 0,
    hasPoints: balances.availablePoints > 0,
    isPremiumUser: userTier === 'Gold' || userTier === 'Platinum',
  }
}

export const useBasicUserDetails = () => {
  const {session} = useAuthentication()

  const user = React.useMemo((): IMkUser | null => session?.authenticatedUser || session?.authorizedUser || null, [session])

  // Support both backend shape (first/last) and Okta/other (firstName/lastName, profile.firstName)
  const first = user?.first ?? (user as any)?.firstName ?? (user as any)?.profile?.firstName ?? ''
  const last = user?.last ?? (user as any)?.lastName ?? (user as any)?.profile?.lastName ?? ''

  return {
    firstName: first,
    lastName: last,
    membershipNumber: user?.memberNumber || ''
  }
}
